import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  can,
  refusalReason,
  type ImportDocAssignment,
  type ImportDocAttachResult,
  type ImportDocMatch,
  type ImportDocMatchFile,
  type Vendor,
} from '../lib/api'
import { fileSize, plural } from '../lib/importWords'
import { Chip } from './StatusChip'
import { ImportError } from './ImportUndo'
import { Picker } from './kit'
import { Icon } from './ui'
import { VendorPicker } from './VendorPicker'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/import.css'

// Contracts and MSAs for the vendors an import brought in (release 3.2).
//
// Three moves: choose the files (or a .zip of them, and a sheet saying which
// file is whose if there is one), see which vendor each belongs to and fix the
// ones we could not place, then attach them. Matching stores nothing; attaching
// sends the same files again with the choices made. Shown after an import and
// from Earlier imports.

type Pick = { vendorId?: string; docType?: string }

const ACCEPT = '.pdf,.docx,.txt,.zip,application/pdf,application/zip,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document'

export function ImportDocuments({ importId, onClose }: { importId: string; onClose?: () => void }) {
  const [files, setFiles] = useState<File[]>([])
  const [sheet, setSheet] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [match, setMatch] = useState<ImportDocMatch | null>(null)
  const [picks, setPicks] = useState<Record<number, Pick>>({})
  const [vendors, setVendors] = useState<Vendor[] | null>(null)
  const [result, setResult] = useState<ImportDocAttachResult | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState<unknown>(null)
  const input = useRef<HTMLInputElement>(null)
  const sheetInput = useRef<HTMLInputElement>(null)

  const allowed = can('vendor.create') && can('evidence.upload')
  const refusal = !can('vendor.create') ? refusalReason('vendor.create') : refusalReason('evidence.upload')

  // The vendor list is read once, and only when a file needs a person to say
  // whose it is.
  const needsPicker = !!match?.files.some((f) => f.ok && !f.vendor)
  useEffect(() => {
    if (!needsPicker || vendors !== null) return
    let live = true
    api
      .vendors({})
      .then((p) => {
        if (live) setVendors(p.vendors)
      })
      .catch(() => {
        if (live) setVendors([])
      })
    return () => {
      live = false
    }
  }, [needsPicker, vendors])

  function choose(list: FileList | null) {
    if (!list || list.length === 0) return
    const next = [...files]
    for (const f of Array.from(list)) {
      if (!next.some((g) => g.name === f.name && g.size === f.size)) next.push(f)
    }
    setFiles(next)
    setMatch(null)
    setPicks({})
    setResult(null)
  }

  async function runMatch() {
    setBusy('match')
    setError(null)
    try {
      const m = await api.matchImportDocuments(importId, files, sheet)
      setMatch(m)
      setPicks({})
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  const assignments = useMemo<ImportDocAssignment[]>(() => {
    if (!match) return []
    const out: ImportDocAssignment[] = []
    match.files.forEach((f, i) => {
      if (!f.ok) return
      const p = picks[i] ?? {}
      const vendorId = p.vendorId ?? f.vendor?.id ?? ''
      if (!vendorId) return
      const docType = p.docType ?? f.docType ?? match.docTypes[0]?.code ?? ''
      out.push({ name: f.name, vendorId, docType, expiresOn: '' })
    })
    return out
  }, [match, picks])

  async function attach() {
    setBusy('attach')
    setError(null)
    try {
      setResult(await api.attachImportDocuments(importId, files, assignments))
    } catch (err) {
      setError(err)
    } finally {
      setBusy('')
    }
  }

  function startAgain() {
    setFiles([])
    setSheet(null)
    setMatch(null)
    setPicks({})
    setResult(null)
    setError(null)
    if (input.current) input.current.value = ''
    if (sheetInput.current) sheetInput.current.value = ''
  }

  const total = files.reduce((n, f) => n + f.size, 0)
  const unplaced = match ? match.files.filter((f, i) => f.ok && !(picks[i]?.vendorId ?? f.vendor?.id)).length : 0
  const refused = match ? match.files.filter((f) => !f.ok).length : 0

  return (
    <section className="panel" aria-labelledby={'docs-' + importId} style={{ marginTop: 12 }}>
      <div className="p1-head" style={{ marginBottom: 8 }}>
        <div>
          <h2 id={'docs-' + importId} style={{ margin: 0 }}>
            Add contracts and MSAs
          </h2>
          <p className="muted" style={{ margin: '2px 0 0' }}>
            We match each file to its vendor by the name or code in the file name. Nothing is sent to the vendors.
          </p>
        </div>
        {onClose && (
          <div className="p1-actions">
            <button type="button" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>

      {result ? (
        <DocumentsSaid result={result} onAgain={startAgain} />
      ) : (
        <>
          {!match && (
            <>
              <div
                className={'drop' + (dragging ? ' on' : '')}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault()
                  setDragging(false)
                  choose(e.dataTransfer.files)
                }}
                onClick={() => input.current?.click()}
                role="button"
                tabIndex={0}
                aria-label="Choose the documents"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    input.current?.click()
                  }
                }}
              >
                <Icon name="file" size={26} />
                <strong>Drop the files here, or click to choose</strong>
                <span>PDF, Word (.docx), text, or a .zip of them</span>
                <input
                  ref={input}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="vh"
                  aria-label="Choose the documents"
                  onChange={(e) => choose(e.target.files)}
                />
              </div>
              {files.length > 0 && (
                <p className="sw-row" style={{ marginTop: 8 }}>
                  <span>
                    {plural(files.length, 'file')} chosen, {fileSize(total)}
                  </span>
                  <button type="button" className="linkish" onClick={startAgain}>
                    Clear
                  </button>
                </p>
              )}
              <div className="field" style={{ marginTop: 12, maxWidth: 420 }}>
                <label htmlFor={'docs-sheet-' + importId}>A sheet saying which file is whose</label>
                <input
                  id={'docs-sheet-' + importId}
                  ref={sheetInput}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => {
                    setSheet(e.target.files?.[0] ?? null)
                    setMatch(null)
                  }}
                />
                <span className="imw-small">If you have one: a column with the file name and a column with the vendor.</span>
              </div>
              <div className="imw-step-foot">
                <button type="button" className="primary" disabled={files.length === 0 || busy !== ''} onClick={runMatch}>
                  {busy === 'match' ? 'Matching' : 'Match them to vendors'}
                </button>
                {files.length === 0 && <p className="imw-why-not">Choose the files first.</p>}
              </div>
            </>
          )}

          {match && (
            <>
              <p className="imw-lead">
                <strong>{plural(match.files.length, 'file')}</strong>
                {unplaced > 0
                  ? ', ' + unplaced + (unplaced === 1 ? ' still needs' : ' still need') + ' a vendor'
                  : ', each matched to a vendor'}
                {refused > 0 ? ', ' + refused + ' cannot be taken' : ''}.
                {match.limits && (
                  <span className="imw-small">
                    Up to {match.limits.maxFiles} files, {match.limits.maxFileMB} MB each and {match.limits.maxTotalMB} MB in all.{' '}
                    {match.limits.types}.
                  </span>
                )}
              </p>
              <ul className="imw-docs">
                {match.files.map((f, i) => (
                  <DocumentRow
                    key={f.name + i}
                    file={f}
                    index={i}
                    importId={importId}
                    docTypes={match.docTypes}
                    pick={picks[i] ?? {}}
                    vendors={vendors}
                    onPick={(p) => setPicks((cur) => ({ ...cur, [i]: { ...(cur[i] ?? {}), ...p } }))}
                  />
                ))}
              </ul>
              <div className="imw-step-foot">
                <button type="button" onClick={() => setMatch(null)}>
                  Back
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={assignments.length === 0 || busy !== '' || !allowed}
                  title={allowed ? undefined : refusal}
                  onClick={attach}
                >
                  {busy === 'attach' ? 'Attaching' : 'Attach ' + plural(assignments.length, 'document')}
                </button>
                {!allowed && <ReasonTip label="Why you cannot attach them" text={refusal} />}
                {allowed && assignments.length === 0 && (
                  <p className="imw-why-not">Choose a vendor for at least one file.</p>
                )}
                {allowed && assignments.length > 0 && unplaced > 0 && (
                  <p className="imw-why-not">Files with no vendor are left out. You can add them later.</p>
                )}
              </div>
            </>
          )}
        </>
      )}
      <ImportError error={error} />
    </section>
  )
}

function DocumentRow({
  file,
  index,
  importId,
  docTypes,
  pick,
  vendors,
  onPick,
}: {
  file: ImportDocMatchFile
  index: number
  importId: string
  docTypes: { code: string; label: string }[]
  pick: Pick
  vendors: Vendor[] | null
  onPick: (p: Pick) => void
}) {
  const typeCode = pick.docType ?? file.docType ?? docTypes[0]?.code ?? ''
  const typeIndex = Math.max(
    0,
    docTypes.findIndex((t) => t.code === typeCode),
  )
  const chosenId = pick.vendorId ?? ''
  const candidates = file.candidates ?? []
  const id = 'doc-' + importId + '-' + index
  return (
    <li>
      <div>
        <span className="imw-docname">{file.name}</span>
        <span className="imw-small">{fileSize(file.size)}</span>
      </div>
      <div>
        {!file.ok ? (
          <>
            <Chip s={{ word: 'Cannot be taken', tone: 'crit', icon: 'cross' }} />
            <span className="imw-small">{file.problem || 'This file cannot be attached. Remove it or choose another file.'}</span>
          </>
        ) : file.vendor ? (
          <>
            <Chip s={{ word: 'Matched', tone: 'ok', icon: 'check' }}>{file.vendor.name}</Chip>
            {file.how && <span className="imw-small">{file.how}</span>}
          </>
        ) : (
          <>
            <VendorPicker
              id={id + '-vendor'}
              label={'Vendor for ' + file.name}
              compact
              value={chosenId}
              onChange={(v) => onPick({ vendorId: v })}
              options={vendors}
              placeholder="Whose is it? Search by name or code"
            />
            {candidates.length > 0 && !chosenId && (
              <span className="imw-cands">
                {candidates.slice(0, 3).map((c) => (
                  <button key={c.id} type="button" className="chip-toggle" onClick={() => onPick({ vendorId: c.id })}>
                    {c.name}
                  </button>
                ))}
              </span>
            )}
            {!chosenId && <span className="imw-small">We could not tell whose this is. Choose the vendor, or leave it out.</span>}
          </>
        )}
      </div>
      <div>
        {file.ok && docTypes.length > 0 && (
          <>
            <label htmlFor={id + '-type'} className="vh">
              {'What kind of document ' + file.name + ' is'}
            </label>
            {/* Nineteen kinds. Chosen by position, as the select was, so the
                kind's code is never put on the page. */}
            <Picker
              bare
              compact
              id={id + '-type'}
              label={'What kind of document ' + file.name + ' is'}
              value={String(typeIndex)}
              clearable={false}
              options={docTypes.map((t, n) => ({ value: String(n), label: t.label }))}
              noun="kind"
              onChange={(n) => onPick({ docType: docTypes[Number(n)]?.code ?? '' })}
            />
          </>
        )}
      </div>
    </li>
  )
}

export function DocumentsSaid({ result, onAgain }: { result: ImportDocAttachResult; onAgain?: () => void }) {
  const problems = result.files.filter((f) => f.problem)
  return (
    <div className="note" role="status">
      <strong>
        {plural(result.attached, 'document')} attached
        {result.skipped > 0 ? ', ' + result.skipped + ' left out' : ''}
      </strong>
      {problems.length > 0 && (
        <ul className="p1-inline-list">
          {problems.map((f, i) => (
            <li key={f.name + i}>
              {f.name}: {f.problem}
            </li>
          ))}
        </ul>
      )}
      {onAgain && (
        <div className="p1-actions" style={{ marginTop: 8 }}>
          <button type="button" onClick={onAgain}>
            Add more documents
          </button>
        </div>
      )}
    </div>
  )
}
